import { getTableColumn, TableColumn } from '@shared/UI';
import { useOrderTable } from './OrderTableProvider';
import { getHeaderClassName } from '@shared/UI/SimpleTable/lib/getHeaderClassName';
import { HeaderCellWithFilter } from '@shared/UI/SimpleTable/components/HeaderCellWithFilter';
import { IconButton } from '@mui/material';
import { useEffect } from 'react';
import dropDownStoreOrder from '../store/drop-down-table-store';
import { ReactComponent as AssignUserIcon } from '../../../shared/assets/svgs/AssignUserIcon.svg';
import { ReactComponent as ExpandIcon } from '../../../shared/assets/svgs/ExpandIcon.svg';

export function getOrderTableColumns(): TableColumn<any>[] {
    const { desc, sortFieldName, arrayWithLogos, onAssignPositionClick, riaScreen } = useOrderTable();

    const handleTableNested = (rowId: number, orderId: number) => {
        const { setOrderRowOpen, orderRowsOpen } = dropDownStoreOrder.getState();
        // Toggle the open state for the specific row
        const newState = !orderRowsOpen[`${rowId}-${orderId}`]; // Invert the current state
        setOrderRowOpen(rowId, orderId, newState);
    };

    useEffect(() => {
        // Whenever the component is mounted or location changes, clear the store state
        return () => {
            dropDownStoreOrder.getState().clearRows(); // Clear the state on route change
        };
    }, []);

    const statusStyles = {
        Assigned: { backgroundColor: '#ECFDF6', color: '#047857', borderColor: '#CEF5E6' },
        Unassigned: { backgroundColor: '#FCF1F1', color: '#B91C1C', borderColor: '#FEE2E2' },
    };

    const userStorage = localStorage.getItem('User');
    let userObject = JSON.parse(userStorage ? userStorage : '');

    const columns: TableColumn<any>[] = [];

    if (userObject?.userRole === 4) {
        columns.push(
            getTableColumn({
                dataField: 'transactionCount',
                flex: 0.13,
                firstColumn: true,
                headerFormatter: (row) => (
                    <HeaderCellWithFilter
                        title='Total Transactions'
                        icon={undefined}
                        onClick={() => null}
                    />
                ),
                headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'transactionCount')}`,
            }),
        );
    } else {
        columns.push(
            getTableColumn({
                dataField: 'account.verificationRequest.firstName',
                flex: 0.11,
                firstColumn: true,
                headerFormatter: (row) => (
                    <HeaderCellWithFilter
                        title='Account Name'
                        icon={undefined}
                        onClick={() => null}
                    />
                ),
                headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'account.verificationRequest.firstName')}`,
                formatter: (_, row) => {
                    return (
                        <div>
                            {' '}
                            {row.account.verificationRequest?.firstName} {row.account.verificationRequest?.lastName}
                        </div>
                    );
                },
            }),
        );
    }

    columns.push(
        getTableColumn({
            dataField: 'id',
            flex: 0.001,
            headerFormatter: (row) => (
                <HeaderCellWithFilter
                    title='id'
                    icon={undefined}
                    onClick={() => null}
                />
            ),
            style: {
                visibility: 'collapse',
                width: 0,
                height: 0,
                padding: 0,
                margin: 0,
            } as React.CSSProperties,
            headerStyle: { display: 'none' } as React.CSSProperties,
            headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'id')}`,
        }),
        getTableColumn({
            dataField: 'accountId',
            flex: 0.001,
            headerFormatter: (row) => (
                <HeaderCellWithFilter
                    title='accountId'
                    icon={undefined}
                    onClick={() => null}
                />
            ),
            style: {
                visibility: 'collapse',
                width: 0,
                height: 0,
                padding: 0,
                margin: 0,
            } as React.CSSProperties,
            headerStyle: { display: 'none' } as React.CSSProperties,
            headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'accountId')}`,
        }),
    );

    columns.push(
        getTableColumn({
            title: 'ticker',
            flex: riaScreen ? 0.16 : 0.18,
            formatter: (_, row) => {
                return (
                    <div className='positions-table-cell'>
                        {!!arrayWithLogos.find((item) => item.symbol === row.symbol)?.link ? (
                            <img
                                className='positions-table-cell__logos'
                                src={arrayWithLogos.find((item) => item.symbol === row.symbol)?.link}
                                alt='logo'
                            />
                        ) : (
                            <div className='positions-table-cell__logos placeholder' />
                        )}
                        <span>{row.symbol as string}</span>
                    </div>
                );
            },
            headerClassName: `positions-table-header ${getHeaderClassName(sortFieldName, desc, 'symbol')}`,
        }),
    );

    columns.push(
        getTableColumn({
            dataField: 'quantity',
            flex: riaScreen ? 0.12 : 0.13,
            firstColumn: true,
            headerFormatter: (row) => (
                <HeaderCellWithFilter
                    title='Quantity'
                    icon={undefined}
                    onClick={() => null}
                />
            ),
            headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'quantity')}`,
        }),
    );

    columns.push(
        getTableColumn({
            dataField: 'updatedPrice',
            flex: riaScreen ? 0.09 : 0.12,
            firstColumn: true,
            headerFormatter: (row) => (
                <HeaderCellWithFilter
                    title={'Price'}
                    icon={undefined}
                    onClick={() => null}
                />
            ),
            headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'updatedPrice')}`,
        }),
    );

    {
        riaScreen &&
            columns.push(
                getTableColumn({
                    dataField: 'status',
                    flex: 0.13,
                    headerFormatter: (row) => (
                        <HeaderCellWithFilter
                            title={String(row)}
                            icon={undefined}
                            onClick={() => null}
                        />
                    ),
                    headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'status')}`,
                    formatter: (_, row) => {
                        const status = row.remainingQuantity === 0 ? 'Assigned' : 'Unassigned';

                        // Use status to get the corresponding style
                        const style = statusStyles[status] || {
                            backgroundColor: '#E4E4E4',
                            borderColor: '#E4E4E4',
                            color: '#000',
                        };
                        return (
                            <span
                                style={{
                                    display: 'inline-block',
                                    padding: '4px 10px',
                                    borderRadius: '8px',
                                    backgroundColor: style.backgroundColor,
                                    color: style.color,
                                    border: `1px solid ${style.borderColor}`,
                                    fontWeight: 500,
                                }}
                            >
                                {status === 'Assigned' ? 'Assigned' : 'Un-Assigned'}
                            </span>
                        );
                    },
                }),
            );
    }

    columns.push(
        getTableColumn({
            dataField: 'side',
            flex: riaScreen ? 0.09 : 0.06,
            headerFormatter: (row) => (
                <HeaderCellWithFilter
                    title='Type'
                    icon={undefined}
                    onClick={() => null}
                />
            ),
            headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'status')}`,
            formatter: (_, row) => {
                const status = row.side === 'Buy' ? 'Assigned' : 'Unassigned';

                // Use status to get the corresponding style
                const style = statusStyles[status] || {
                    backgroundColor: '#E4E4E4',
                    borderColor: '#E4E4E4',
                    color: '#000',
                };
                return (
                    <span
                        style={{
                            display: 'inline-block',
                            padding: '4px 10px',
                            borderRadius: '8px',
                            backgroundColor: style.backgroundColor,
                            color: style.color,
                            border: `1px solid ${style.borderColor}`,
                            fontWeight: 500,
                        }}
                    >
                        {row.side}
                    </span>
                );
            },
        }),
    );

    columns.push(
        getTableColumn({
            dataField: 'open', // You can keep this as a placeholder, it won't affect the data
            flex: 0.15, // Adjust the flex value to fit your table layout
            headerFormatter: () => (
                <HeaderCellWithFilter
                    title='Actions'
                    icon={undefined}
                    onClick={() => null}
                />
            ),
            headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'Delete')}`,
            formatter: (_, row) => (
                <div style={{ display: 'flex' }}>
                    <div
                        style={{
                            width: userObject.userRole === 4 ? '50%' : '0%',
                            textAlignLast: 'right',
                        }}
                    >
                        {row.remainingQuantity !== 0 && userObject.userRole === 4 && (
                            <IconButton
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onAssignPositionClick(row);
                                }}
                                color='primary'
                                style={{ padding: '0px 15px' }}
                            >
                                <AssignUserIcon />
                            </IconButton>
                        )}
                    </div>
                    <div
                        style={{
                            width: userObject.userRole === 4 ? '50%' : '100%',
                            textAlignLast: userObject.userRole === 4 ? 'start' : 'auto',
                        }}
                    >
                        <IconButton
                            onClick={(event) => {
                                event.stopPropagation();
                                handleTableNested(row.accountId as number, row.id as number); // Pass the row ID
                            }}
                            color='primary'
                            style={{ padding: '0px' }}
                        >
                            <ExpandIcon />
                        </IconButton>
                    </div>
                </div>
            ),
        }),
    );
    return columns;
}
